import { useAllModulesConfig, ModuleVisibilityStatus } from './useAuthConfig';

/**
 * Detect if current session has admin preview privileges.
 * Admin preview is granted when:
 *  - logged-in admin (patakare_admin in localStorage), OR
 *  - URL has ?admin_preview=1 (set when admin clicks Preview in Auth Management)
 */
export function isAdminPreview(): boolean {
  if (typeof window === 'undefined') return false;

  // URL query param (sticky via sessionStorage so it survives client-side nav)
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin_preview') === '1') {
      sessionStorage.setItem('patakare_admin_preview', '1');
      return true;
    }
    if (sessionStorage.getItem('patakare_admin_preview') === '1') return true;
  } catch { /* ignore */ }

  return false;
}

export interface ModuleVisibilityResult {
  status: ModuleVisibilityStatus;
  isVisible: boolean;     // safe to show in public UI
  canAccess: boolean;     // safe to render the route
  isAdminOnly: boolean;   // BETA — only admins
}

/**
 * Centralised gate. Use in navbars, category grids, and route guards.
 *
 * Defaults to LIVE if config missing — fully backward compatible.
 */
export function useModuleVisibility(moduleKey: string): ModuleVisibilityResult {
  const config = useAllModulesConfig();
  const status = (config?.[moduleKey]?.visibility_status ?? 'LIVE') as ModuleVisibilityStatus;
  const adminPreview = isAdminPreview();

  switch (status) {
    case 'HIDDEN':
    case 'COMING_SOON':
      return {
        status,
        isVisible: adminPreview,
        canAccess: adminPreview,
        isAdminOnly: true,
      };
    case 'BETA':
      return {
        status,
        isVisible: adminPreview,
        canAccess: adminPreview,
        isAdminOnly: true,
      };
    case 'LIVE':
    default:
      return {
        status: 'LIVE',
        isVisible: true,
        canAccess: true,
        isAdminOnly: false,
      };
  }
}
